import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material';
import { Observable } from 'rxjs';
import { FilterType } from 'src/API';
import { isWithinTimeFrameInDays } from '../core/api/date.util';
import { DynamicActionService } from '../core/api/dynamic-action.service';
import { NavigationService } from '../core/api/navigation.service';
import { mainSideNavSchema } from '../core/schemas/navigation-schema';
import { AppStateService } from '../core/services/app-state.service';
import { FilterGroup, MatchType } from '../filter/filter.model';
import {
  CLINIC_CHANGE_FLASH_MESSAGE_STORAGE_KEY,
  ClinicChangeFlashMessage
} from '../logged-in-navbar/change-clinic-modal/clinic-change-flash-message';
import { CardBottomColor } from './dashboard-number-card/dashboard-number-card.component';
import {
  DashboardNumberCardStatConfig,
  StatisticsConfig,
  TimeFrameInDays,
  timeFrameToCardConfigMap
} from './dashboard.model';

@Component({
  selector: 'csi-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {
  public readonly translocoFileKey = 'dashboard.';
  public isPatientAppActive$: Observable<boolean>;
  public loading = true;
  cards = {};

  statsConfig: StatisticsConfig[] = [
    {
      serviceName: 'FilterService',
      functionName: 'queryFilterData',
      params: [FilterType.questionnaireRequest],
      title: 'Questionnaires Sent',
      timeFrameSelection: [
        TimeFrameInDays.WEEK,
        TimeFrameInDays.MONTH,
        TimeFrameInDays.QUARTER,
        TimeFrameInDays.YEAR,
        TimeFrameInDays.ALL
      ],
      cardBottomColor: 'blue',
      filterBy: 'createdAt',
      tooltip: 'Represents the total number of questionnaires sent to patients.'
    },
    {
      serviceName: 'FilterService',
      functionName: 'queryFilterData',
      params: [FilterType.questionnaireRequest],
      title: 'Questionnaires Completed',
      timeFrameSelection: [
        TimeFrameInDays.WEEK,
        TimeFrameInDays.MONTH,
        TimeFrameInDays.QUARTER,
        TimeFrameInDays.YEAR,
        TimeFrameInDays.ALL
      ],
      cardBottomColor: 'blue',
      filterBy: 'completedAt',
      tooltip: 'Represents the number of questionnaires completed by patients.'
    },
    {
      serviceName: 'FilterService',
      functionName: 'queryFilterData',
      params: [FilterType.patient],
      timeFrameSelection: [
        TimeFrameInDays.WEEK,
        TimeFrameInDays.MONTH,
        TimeFrameInDays.QUARTER,
        TimeFrameInDays.YEAR,
        TimeFrameInDays.ALL
      ],
      title: 'New Patients',
      cardBottomColor: 'green',
      filterBy: 'createdAt',
      tooltip: 'Represents the number of newly added patients.'
    },
    {
      serviceName: 'FilterService',
      functionName: 'queryFilterData',
      params: [FilterType.assessment],
      title: 'New Assessments',
      timeFrameSelection: [
        TimeFrameInDays.WEEK,
        TimeFrameInDays.MONTH,
        TimeFrameInDays.QUARTER,
        TimeFrameInDays.YEAR,
        TimeFrameInDays.ALL
      ],
      cardBottomColor: 'orange',
      filterBy: 'createdAt',
      tooltip: 'Represents the number of new assessments created.'
    },
    {
      serviceName: 'FilterService',
      functionName: 'queryFilterData',
      params: [FilterType.intakeForm],
      timeFrameSelection: [
        TimeFrameInDays.WEEK,
        TimeFrameInDays.MONTH,
        TimeFrameInDays.QUARTER,
        TimeFrameInDays.YEAR,
        TimeFrameInDays.ALL
      ],
      title: 'Intake Forms Received',
      cardBottomColor: 'purple',
      filterBy: 'createdAt',
      tooltip: 'Represents the number of intake forms submitted by patients.'
    }
  ];

  constructor(
    public navigationService: NavigationService,
    private dynamicActionService: DynamicActionService,
    private snackBar: MatSnackBar,
    private appStateService: AppStateService
  ) {
    this.isPatientAppActive$ = this.appStateService.isPatientAppActive$;
  }

  ngOnInit() {
    this.showPendingClinicChangeSuccessMessage();
    this.statsConfig.forEach(config => {
      this.generateStatistics(config);
    });
    this.navigationService.updateSideNavSchema(mainSideNavSchema);
  }

  private showPendingClinicChangeSuccessMessage() {
    const rawMessage = sessionStorage.getItem(CLINIC_CHANGE_FLASH_MESSAGE_STORAGE_KEY);
    if (!rawMessage) {
      return;
    }

    sessionStorage.removeItem(CLINIC_CHANGE_FLASH_MESSAGE_STORAGE_KEY);

    try {
      const flashMessage = JSON.parse(rawMessage) as ClinicChangeFlashMessage;
      if (!flashMessage || !flashMessage.message) {
        return;
      }

      this.snackBar.open(flashMessage.message, '', {
        duration: 4000,
        verticalPosition: 'bottom'
      });
    } catch (error) {
      // Ignore malformed flash payloads so a bad session value cannot break home.
    }
  }

  private generateFilterGroup(field: string, timeFrameInDays: number): FilterGroup {
    const dateToday = new Date();

    // Calculate the date for two weeks ago
    const startDate = new Date();
    startDate.setDate(dateToday.getDate() - timeFrameInDays * 2);

    return {
      type: MatchType.and,
      filters: [
        {
          field,
          operator: 'greaterThanOrEqualTo',
          searchValue: startDate.toISOString()
        },
        {
          field,
          operator: 'lessThanOrEqualTo',
          searchValue: dateToday.toISOString()
        }
      ]
    };
  }
  public generateStatistics(config: StatisticsConfig) {
    if (config.timeFrameSelection.length === 0) {
      console.warn('time frame selection is 0');
      return;
    }
    const dateToday = new Date();

    const startDate = new Date();
    const timeFrameInDays = Math.max(...config.timeFrameSelection.map(t => t as number)) * 2;
    startDate.setDate(dateToday.getDate() - timeFrameInDays);

    this.dynamicActionService
      .performAction({
        serviceName: config.serviceName,
        functionName: config.functionName,
        params: [...config.params, []]
      })
      .subscribe(data => {
        this.cards[config.title] = this.generateNumberCardConfig(
          data,
          config.filterBy,
          config.title,
          config.cardBottomColor,
          config.timeFrameSelection || [],
          config.tooltip
        );
        this.loading = false;
      });
  }

  public generateNumberCardConfig(
    data: any[],
    filterBy: string,
    title: string,
    cardBottomColor: CardBottomColor = 'none',
    timeFrameSelection: TimeFrameInDays[] = [],
    tooltip?: string
  ) {
    let multiDateRangeConfig: DashboardNumberCardStatConfig[] = this.generateMultiDateRangeConfig(
      data,
      filterBy,
      timeFrameSelection
    );

    return {
      title,
      cardBottomColor,
      multiStatConfig: multiDateRangeConfig,
      tooltip
    };
  }

  public generateMultiDateRangeConfig(
    data: any[],
    filterBy: string,
    timeFrameSelection: TimeFrameInDays[] = []
  ) {
    const dateToday = new Date();
    const startDate = new Date();
    let multiDateRangeConfig: DashboardNumberCardStatConfig[] = [];

    if (timeFrameSelection.length > 0) {
      for (let timeFrame of timeFrameSelection) {
        let dataFilteredByLastTimeFrame;
        startDate.setDate(dateToday.getDate() - timeFrame * 2);
        dataFilteredByLastTimeFrame = data.filter(value =>
          isWithinTimeFrameInDays(startDate, new Date(value[filterBy]), timeFrame)
        );

        let dateFilteredByCurrentTimeFrame;
        dateFilteredByCurrentTimeFrame = data.filter(value =>
          isWithinTimeFrameInDays(dateToday, new Date(value[filterBy]), timeFrame)
        );

        const stat = dateFilteredByCurrentTimeFrame.length;
        const statChange =
          dateFilteredByCurrentTimeFrame.length - dataFilteredByLastTimeFrame.length;
        const statPercentChanged =
          ((dateFilteredByCurrentTimeFrame.length - dataFilteredByLastTimeFrame.length) /
            dataFilteredByLastTimeFrame.length) *
          100;

        multiDateRangeConfig.push({
          label: timeFrameToCardConfigMap[timeFrame].label,
          stat,
          statChange,
          statPercentChanged,
          subtitle: timeFrameToCardConfigMap[timeFrame].subtitle
        });
      }
    }
    return multiDateRangeConfig;
  }
}
